"use client";

import { LocaleLink, useTranslator } from "~/i18n/provider";
import { useAccountIdentity } from "~/components/auth/signed-in-provider";

/**
 * P2-01 · Header account entry. Guest → sign-in link; logged-in → plain link
 * to /account (no dropdown — P2-DB03 r2: treat the account menu as out of
 * scope until one is designed; the /account route itself lands in P5).
 *
 * P17A-06 · It hydrates rather than being handed a resolved session.
 *
 * `Header` used to `auth()` and pass `userName` down. That made the header's
 * HTML depend on who was asking, on every storefront render, for a link and a
 * name — and it is the header that sits in the layout above every cacheable
 * page. Now the layout passes a header-only cookie probe into
 * `SignedInProvider`, which confirms it against `/api/auth/session` on mount
 * and carries the display name back (see that file for the whole argument).
 *
 * So the three states are:
 *
 *  - **no session cookie** — renders the sign-in link and never changes. This
 *    is the HTML a shared cache stores, and it is the same for everybody.
 *  - **cookie present, not yet confirmed** — renders the account link under
 *    the generic "Account" label, a string this app already ships. It points
 *    at the right place immediately; only the name arrives late.
 *  - **confirmed** — the name replaces the generic label.
 *
 * A stale or revoked cookie therefore shows an account link for one round trip
 * and then falls back to sign-in, rather than the reverse (a signed-in shopper
 * shown "Sign in" on a page they are signed in on). Neither state grants
 * anything: `/account` re-checks the real session.
 */
export function AccountButton() {
  const t = useTranslator();
  const { signedIn, name } = useAccountIdentity();

  const icon = (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      className="h-5 w-5"
    >
      <circle cx="12" cy="8.5" r="3.5" />
      <path d="M4.5 19.5a7.5 7.5 0 0 1 15 0" />
    </svg>
  );

  const className =
    "flex items-center gap-2 rounded-md p-2 text-fg hover:bg-surface-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)]";

  if (!signedIn) {
    return (
      <LocaleLink href="/sign-in" className={className}>
        {icon}
        <span className="hidden text-sm lg:inline">{t("common.signIn")}</span>
      </LocaleLink>
    );
  }

  return (
    <LocaleLink
      href="/account"
      className={className}
      aria-label={name ? t("nav.accountLabel", { name }) : t("nav.account")}
    >
      {icon}
      <span className="hidden max-w-32 truncate text-sm lg:inline">
        {name ?? t("nav.account")}
      </span>
    </LocaleLink>
  );
}
